import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { StudentRecord } from "../../types";
import { GetStudentList } from "../../services/endpoints/student.endpoint";
import { logout } from "./authSlice";
import type { RootState } from "../store";

export interface StudentState {
  records: StudentRecord[];
  isLoading: boolean;
  error: string | null;
}

const initialState: StudentState = {
  records: [],
  isLoading: false,
  error: null,
};

export const getStudentsThunk = createAsyncThunk<
  StudentRecord[],
  string | { teacherId: string; force?: boolean },
  { state: RootState; rejectValue: string }
>(
  "student/getStudents",
  (param, { rejectWithValue }) => {
    const teacherId = typeof param === "string" ? param : param.teacherId;
    const selectQuery = `select id, First_Name, Middle_Name, Last_Name, Email, Phone, Student_Number2, Gender, Centre, Mandal, Month_of_Birth, Year_of_Birth, Parent_First_Name, Parent_Email, Parent_Last_Name, Parent_Mobile, Allocated_Teacher, Batch_Day from Contacts where Allocated_Teacher = '${teacherId}' order by id asc limit 200`;
    return GetStudentList({ selectQuery })
      .then((res) => res.data.records)
      .catch((error: any) => rejectWithValue(error.message));
  },
  {
    condition: (param, { getState }) => {
      const { student } = getState();
      const force = typeof param === "object" && param.force;
      if (force) return true;
      return student.records.length === 0 && !student.isLoading && !student.error;
    },
  }
);

const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    clearStudents: (state) => {
      state.records = [];
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStudentsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStudentsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
        state.error = null;
      })
      .addCase(getStudentsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(logout, (state) => {
        state.records = [];
        state.isLoading = false;
        state.error = null;
      });
  },
});

export const { clearStudents } = studentSlice.actions;
export default studentSlice.reducer;
